import { api } from "./api";

export type PartnerCategory = "traditional_agent" | "online_agent" | "social_media" | "corporate";
export type SettlementMode = "prepay" | "credit_monthly" | "credit_per_trip";
// B2B-D · 结算周期（与 settlement_mode 是两条不同的轴）。老板 2026-07-31 拍板：
// UI 现阶段只提供「月结/单结」两个选项（DB 仍宽松保留 weekly/biweekly 存量值·不在此暴露）。
export type SettlementCycle = "monthly" | "per_order";
export type Health = "good" | "watch" | "blocked";
export type CreditRequestStatus = "pending" | "approved" | "rejected";

export interface Partner {
  id: string;
  /** IDFILL-1 · 同行编号 PTR00001 */
  code?: string | null;
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  category: PartnerCategory;
  settlement_mode: SettlementMode;
  settlement_cycle: SettlementCycle;
  credit_limit_cents: number;
  credit_used_cents: number;
  credit_available_cents: number;
  /** CREDIT-1 · 逾期超限已停新单 / 逾期金额（来自唯一额度口径） */
  credit_blocked?: boolean;
  credit_overdue_cents?: number;
  health: Health;
  last_order_at: string | null;
  am_user_id: string | null;
  notes: string | null;
  tax_id?: string | null;
  billing_address?: string | null;
  contract_start_date?: string | null;
  api_enabled?: boolean;
  created_at: string;
}

export interface PartnerCreate {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  category: PartnerCategory;
  settlement_mode: SettlementMode;
  settlement_cycle?: SettlementCycle;
  credit_limit_cents: number;
  notes?: string;
  tax_id?: string;
  billing_address?: string;
  contract_start_date?: string;
  /** WIZDRAFT-2 · 向导里存过草稿：建成功后后端同事务删这条草稿；没存过 = 不带 */
  wizard_draft_id?: string;
}

export interface CreditRequest {
  id: string;
  partner_id: string;
  additional_credit_cents: number;
  reason: string;
  duration_days: number;
  status: CreditRequestStatus;
  submitted_by: string;
  submitted_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
}

export interface Settlement {
  id: string;
  partner_id: string;
  period_start: string;
  period_end: string;
  orders_count: number;
  gross_cents: number;
  fees_cents: number;
  net_cents: number;
  status: "draft" | "sent" | "paid";
  created_at: string;
}

export const PARTNER_CATEGORY_LABEL: Record<PartnerCategory, string> = {
  traditional_agent: "传统旅行社",
  online_agent: "OTA 平台",
  social_media: "社媒达人",
  corporate: "企业客户",
};

export const SETTLEMENT_MODE_LABEL: Record<SettlementMode, string> = {
  prepay: "预付",
  credit_monthly: "月结授信",
  credit_per_trip: "单团授信",
};

// B2B-D · 结算周期标签（同行站对账单据此决定视图，见 partner-portal Statement 页）
export const SETTLEMENT_CYCLE_LABEL: Record<SettlementCycle, string> = {
  monthly: "月结",
  per_order: "单结",
};

export const HEALTH_LABEL: Record<Health, string> = {
  good: "良好",
  watch: "关注",
  blocked: "停用",
};

// W3 · outstanding 实时聚合 · 4 stat tile · credit approval queue
export interface OutstandingSummary {
  partner_id: string;
  invoices_outstanding_cents: number;
  receivables_outstanding_cents: number;
  total_outstanding_cents: number;
  overdue_cents: number;
  overdue_count: number;
  credit_limit_cents: number;
  credit_available_cents: number;
}

export interface B2BStats {
  total_partners: number;
  active_partners: number;
  pending_credit_requests: number;
  total_outstanding_cents: number;
  overdue_outstanding_cents: number;
  by_category: Record<PartnerCategory, number>;
}

// 客户健康度看板（对齐 canvas 客户列表 / 客户经理工作台）· 分级动态可配
export type PartnerTier = string;   // 分级 code（动态·来自 partner_tiers 配置）
export type PartnerActivity = "active" | "dormant" | "suspended";
export const ACTIVITY_LABEL: Record<PartnerActivity, string> = { active: "活跃", dormant: "休眠", suspended: "暂停" };
// 已知 code 配色（自定义级别走默认灰）
const TIER_COLOR: Record<string, string> = { vip: "bg-violet-100 text-violet-700", normal: "bg-blue-100 text-blue-700", trial: "bg-slate-100 text-slate-600" };
export const tierChip = (code: string) => TIER_COLOR[code] ?? "bg-teal-100 text-teal-700";
export const tierAvatar = (act: PartnerActivity) => ({ active: "bg-emerald-500", dormant: "bg-amber-500", suspended: "bg-rose-500" }[act]);

export interface Tier {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  min_monthly_orders: number;
  min_annual_gmv_cents: number;
  credit_min_cents: number;
  credit_max_cents: number;
  baseline_markup_pct: number;
  settlement_terms: string;
  is_active: boolean;
}
export type TierUpsert = Omit<Tier, "id">;
export interface TierCount { code: string; name: string; count: number; }

export interface BoardRow {
  id: string;
  /** IDFILL-1 · 同行编号 PTR00001 */
  code?: string | null;
  name: string;
  tier: string;
  tier_name: string;
  activity: PartnerActivity;
  order_count: number;
  last_order_days: number | null;
  used_cents: number;
  limit_cents: number;
}
export interface BoardRecentOrder {
  order_no: string;
  created_at: string;
  customer: string;
  pax: number;
  amount_cents: number;
  status: string;
}
export interface Board {
  total: number;
  tier_counts: TierCount[];
  suspended: number;
  month_new: number;
  pending_credits: number;
  month_revenue_cents: number;
  revenue_pct: number;
  rows: BoardRow[];
  recent_orders: BoardRecentOrder[];
}

export interface PartnerStats {
  tier: string;
  tier_name: string;
  tier_override: boolean;
  credit_in_range: boolean;
  credit_range_hint: string | null;
  activity: PartnerActivity;
  health_score: number;
  total_orders: number;
  total_gmv_cents: number;
  avg_order_cents: number;
  complaint_rate_pct: number;
  last_order_days: number | null;
  score_activity: number;
  score_payment: number;
  score_complaint: number;
  score_am: number;
  representative: boolean;
  recent_orders: BoardRecentOrder[];
}

export interface SettlementPartnerRow {
  id: string;
  name: string;
  ar_cents: number;
  collected_cents: number;
  overdue_cents: number;
  status: "collected" | "partial" | "overdue" | "blocked";
  // CREDIT-1 · 结算方式 / 授信 / 已用 / 逾期天数 / 预收余额 / 停单（样稿 ⑤）
  mode: SettlementMode;
  limit_cents: number;
  used_cents: number;
  overdue_days: number;
  prepaid_balance_cents: number;
  blocked: boolean;
}
export interface SettlementBoard {
  ar_total_cents: number;
  ar_collected_cents: number;
  ar_overdue_cents: number;
  overdue_partners: number;
  pending_credits: number;
  collected_pct: number;
  partners: SettlementPartnerRow[];
  month_collected_cents: number;
  blocked_partners: number;
}

/** PARTNER-ACC-1 · 同行站登录账号（不含任何哈希 / 凭据） */
export interface PartnerAccount {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  /** developer = 开放平台「技术对接」账号（OPENAPI-2；只能登录开放平台，不占 1 主 1 副名额） */
  role: "primary" | "secondary" | "developer";
  status: "invited" | "active" | "disabled";
  last_login_at: string | null;
  created_at: string;
  /** 邀请过期时间（仅「已邀请」状态有；邀请有效期 7 天，发出日 = 过期 − 7 天） */
  invite_expires_at?: string | null;
  /** 锁定到几点（仅正被锁时有）· locked = 现在是否被锁 */
  locked_until?: string | null;
  locked?: boolean;
}

const ACC = (partnerId: string) => `/api/v1/channel-partners/${partnerId}/accounts`;
export const partnerAccountsApi = {
  list: (partnerId: string) => api<PartnerAccount[]>(ACC(partnerId)),
  create: (partnerId: string, body: { full_name: string; email: string; phone?: string; role: "primary" | "secondary" }) =>
    api<PartnerAccount>(ACC(partnerId), { method: "POST", body: JSON.stringify(body) }),
  resendInvite: (partnerId: string, aid: string) => api<PartnerAccount>(`${ACC(partnerId)}/${aid}/resend-invite`, { method: "POST" }),
  resetPassword: (partnerId: string, aid: string) => api<PartnerAccount>(`${ACC(partnerId)}/${aid}/reset-password`, { method: "POST" }),
  setStatus: (partnerId: string, aid: string, status: "active" | "disabled") =>
    api<PartnerAccount>(`${ACC(partnerId)}/${aid}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  unlock: (partnerId: string, aid: string) => api<PartnerAccount>(`${ACC(partnerId)}/${aid}:unlock`, { method: "POST" }),
};

export const partnersApi = {
  board: () => api<Board>("/api/v1/channel-partners/_board"),
  settlementBoard: () => api<SettlementBoard>("/api/v1/channel-partners/_settlement-board"),
  partnerStats: (id: string) => api<PartnerStats>(`/api/v1/channel-partners/${id}/stats`),
  list: (params?: { category?: PartnerCategory; health?: Health }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set("category", params.category);
    if (params?.health) qs.set("health", params.health);
    const suffix = qs.toString();
    return api<Partner[]>(`/api/v1/channel-partners${suffix ? `?${suffix}` : ""}`);
  },
  get: (id: string) => api<Partner>(`/api/v1/channel-partners/${id}`),
  create: (payload: PartnerCreate) =>
    api<Partner>("/api/v1/channel-partners", { method: "POST", body: JSON.stringify(payload) }),
  update: (id: string, payload: Partial<PartnerCreate> & { reason_code?: string; reason?: string }) =>
    api<Partner>(`/api/v1/channel-partners/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  setStatus: (id: string, status: "active" | "suspended") =>
    api<Partner>(`/api/v1/channel-partners/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  // 分级 override：指定 code=手动锁定 · null=恢复自动
  setTier: (id: string, tierCode: string | null) =>
    api<Partner>(`/api/v1/channel-partners/${id}`, { method: "PATCH", body: JSON.stringify({ tier_code: tierCode, tier_override: tierCode !== null }) }),
  // 分级配置 CRUD
  listTiers: () => api<Tier[]>("/api/v1/channel-partners/_tiers"),
  createTier: (payload: TierUpsert) => api<Tier>("/api/v1/channel-partners/_tiers", { method: "POST", body: JSON.stringify(payload) }),
  updateTier: (id: string, payload: TierUpsert) => api<Tier>(`/api/v1/channel-partners/_tiers/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteTier: (id: string) => api<void>(`/api/v1/channel-partners/_tiers/${id}`, { method: "DELETE" }),
  delete: (id: string) =>
    api<void>(`/api/v1/channel-partners/${id}`, { method: "DELETE" }),

  // W3 stats + outstanding
  stats: () => api<B2BStats>("/api/v1/channel-partners/_stats"),
  outstanding: (id: string) =>
    api<OutstandingSummary>(`/api/v1/channel-partners/${id}/outstanding`),

  // Credit
  listCredits: (id: string) =>
    api<CreditRequest[]>(`/api/v1/channel-partners/${id}/credits`),
  submitCredit: (id: string, payload: { additional_credit_cents: number; reason: string; duration_days?: number }) =>
    api<CreditRequest>(`/api/v1/channel-partners/${id}/credit-request`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  // REASON-1 · 原因放请求体（reason_code + reason），不再走 ?note=；拒绝必填、批准选填
  decideCredit: (partnerId: string, rid: string, approve: boolean, reason?: { reason_code?: string; reason?: string }) =>
    api<CreditRequest>(
      `/api/v1/channel-partners/${partnerId}/credit-request/${rid}/approve?approve=${approve}`,
      { method: "PATCH", body: JSON.stringify(reason ?? {}) },
    ),

  // W3 · approval queue (cross-partner)
  listAllCreditRequests: (statusFilter?: CreditRequestStatus) => {
    const qs = statusFilter ? `?status_filter=${statusFilter}` : "";
    return api<CreditRequest[]>(`/api/v1/channel-partners/_credit-requests${qs}`);
  },
  decideCreditQueue: (rid: string, approve: boolean, reason?: { reason_code?: string; reason?: string }) =>
    api<CreditRequest>(
      `/api/v1/channel-partners/_credit-requests/${rid}/decide?approve=${approve}`,
      { method: "PATCH", body: JSON.stringify(reason ?? {}) },
    ),

  // Settlements
  listSettlements: (id: string) =>
    api<Settlement[]>(`/api/v1/channel-partners/${id}/settlements`),
  createSettlement: (id: string, payload: { period_start: string; period_end: string }) =>
    api<Settlement>(`/api/v1/channel-partners/${id}/settlements`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};
